// The studio's two ways of asking for data, both of them thin adapters over
// `useBiDashboardQuery`.
//
// There is deliberately no second render loop here. Everything that makes the
// dashboard page's loop correct — request supersession, the held-settle buffer,
// the cross-session result-key memory, polling so Pusher is an optimisation
// rather than a dependency, the poll deadline, the dimmed-stale rule — is
// exactly as necessary for a canvas as for a dashboard, and rewriting a thinner
// version of it for the editor would be rewriting its bugs too. What differs is
// the URL and the id the settle event carries, and `BiQuerySourceOptions` is
// where those two facts live.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { BiQueryResponse, ChartSpec, FilterState } from "@bi/shared/types";

import { annotationIdsByChart } from "./annotation-marks";
import { previewStudioChart, queryChartsAtPath, queryStudioDashboardCharts } from "./api";
import { type ChartRenderState, useBiDashboardQuery } from "./bi-query";
import { isChartComposed } from "./built-chart";

/** `value`, but only after it has stopped changing for `ms`.
 *
 *  A canvas previews on every edit, and every preview is a request against the
 *  reader's own daily budget plus a BigQuery dry run. Typing a chart title
 *  should not cost eleven of those. The debounce is what turns "previews on
 *  every edit" from a description of the behaviour into an affordable one. */
export function useDebounced<T>(value: T, ms = 600): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

const NO_CHARTS: string[] = [];

export interface StudioPreviewResult {
  state: ChartRenderState;
  /** The previewed chart's MARK SET, when it declares annotations. `null` means
   *  the chart declares none — a mark set that FAILED comes back as a state
   *  with `status: "error"`, so the tile can say the marks are missing rather
   *  than quietly drawing a line without them. */
  annotation: ChartRenderState | null;
  /** What the dry run said this chart would scan, or `null` before the first
   *  answer. Surfaced rather than kept, because the rail refuses above the
   *  ceiling and watching the number approach it is how a person learns to
   *  narrow a chart BEFORE it is refused. */
  bytesEstimate: number | null;
  retry: () => void;
}

/**
 * Draw one transient chart while somebody is still composing it.
 *
 * The chart usually exists nowhere yet, so the whole spec rides the request.
 * Server-side it is parked in a short-TTL cache entry and only the KEY goes on
 * the queue, so the worker re-fetches and re-validates it. The response is
 * shaped like one chart's entry in a `BiQueryResponse`, which lets it ride the
 * dashboard loop unchanged.
 *
 * The backend wraps a preview in a synthetic dashboard whose uuid is the
 * CHART's uuid (`single_chart_dashboard` in `bi/core/studio_query.py`), so that
 * is the id the settle event carries. Passing the row id or a fresh uuid here
 * leaves every preview spinning.
 */
export function useStudioPreview(
  chart: ChartSpec | null,
  filters: FilterState,
  employeeEmail: string,
): StudioPreviewResult {
  const [bytesEstimate, setBytesEstimate] = useState<number | null>(null);
  const [annotationId, setAnnotationId] = useState<string | null>(null);
  const chartRef = useRef(chart);
  chartRef.current = chart;

  const runQuery = useCallback(
    async (_ids: readonly string[], currentFilters: FilterState): Promise<BiQueryResponse> => {
      const current = chartRef.current;
      if (!current) return { requestId: "", charts: [] };
      const resp = await previewStudioChart({ chart: current, filters: currentFilters });
      setBytesEstimate(resp.bytesEstimate ?? null);
      // The mark set rides the SAME response and the same settle path, so it
      // joins `charts` as an ordinary second entry rather than needing a
      // delivery mechanism of its own. Its id is the server's, not one this
      // client recomputed.
      setAnnotationId(resp.annotation?.chartId ?? null);
      return {
        requestId: resp.requestId,
        charts: [
          {
            chartId: resp.chartId,
            status: resp.status,
            resultKey: resp.resultKey,
            error: resp.error ?? null,
          },
          ...(resp.annotation ? [resp.annotation] : []),
        ],
      };
    },
    [],
  );

  // The revision is what re-issues the preview when the SPEC changed rather
  // than the filters — the tile keeps its uuid across every edit, so nothing
  // else about the request identity moves.
  const revision = useMemo(() => (chart ? JSON.stringify(chart) : ""), [chart]);
  // A chart nobody has picked a measure for yet is not a chart to spend a query
  // on. `isChartComposed`, not the validator: "not finished" is not "not
  // allowed", and a brand new tile is always the former for a moment.
  const ready = chart !== null && isChartComposed(chart);
  const chartIds = useMemo(
    () => (ready && chart ? [chart.uuid] : NO_CHARTS),
    [ready, chart],
  );

  const { charts, retryChart } = useBiDashboardQuery(
    chart ? `preview:${chart.uuid}` : "",
    chartIds,
    filters,
    employeeEmail,
    { runQuery, eventDashboardId: chart?.uuid ?? "", revision },
  );

  const state: ChartRenderState = chart
    ? (charts[chart.uuid] ?? { status: "loading" })
    : { status: "loading" };
  const annotation = annotationId ? (charts[annotationId] ?? { status: "loading" as const }) : null;
  const retry = useCallback(() => {
    if (chart) retryChart(chart.uuid);
  }, [chart, retryChart]);
  return { state, annotation, bytesEstimate, retry };
}

/**
 * Draw a SAVED studio dashboard.
 *
 * `rowId` addresses the route (`/api/bi/mine/dashboards/{rowId}/query`);
 * `specUuid` is `spec.spec.uuid`, the id the settle event carries. They are
 * different values and both are needed — the row is where the document is
 * stored, the spec uuid is what the fan-out publishes under.
 */
export function useStudioDashboardCharts(
  rowId: string,
  specUuid: string,
  chartIds: readonly string[],
  filters: FilterState,
  employeeEmail: string,
  /** Where to POST. Defaults to the studio row's own query route; a TEMPLATE
   *  passes the `queryPath` its response named, because an authored template
   *  answers on `/api/bi/dashboards/{id}/query` and deciding that here would
   *  put the routing rule in two places. */
  queryPath?: string,
) {
  // chartId -> its mark set's chart id, recovered from the response's own
  // ordering (see `annotationIdsByChart`). Held in state rather than derived at
  // render because only the response knows it, and it does not change between
  // responses for an unchanged layout.
  const [annotationIds, setAnnotationIds] = useState<Record<string, string>>({});
  const runQuery = useCallback(
    async (ids: readonly string[], currentFilters: FilterState) => {
      const body = { chartIds: [...ids], filters: currentFilters, forceRefresh: false };
      const resp = queryPath
        ? await queryChartsAtPath(queryPath, body)
        : await queryStudioDashboardCharts(rowId, body);
      setAnnotationIds(annotationIdsByChart(ids, resp.charts));
      return resp;
    },
    [rowId, queryPath],
  );
  const result = useBiDashboardQuery(rowId, chartIds, filters, employeeEmail, {
    runQuery,
    eventDashboardId: specUuid,
  });
  return { ...result, annotationIds };
}
